/**
 * Per-domain API base URLs — the single domain map for both clients.
 *
 * Until 2026-08-25 this was two hand-synchronised files: `frontend/web/src/lib/api/
 * config.ts` (179 lines) and `frontend/mobile/src/lib/api/config.ts` (161 lines), the
 * latter's own comment admitting the sync was manual. A domain pointing at
 * the wrong root does not fail loudly — it 404s, or worse, lands on a
 * DIFFERENT router that happens to own that prefix and answers with its own
 * shape. That happened in production: `expenses` pointed at the rent
 * `PropertyExpense` router until it was found on 2026-08-22 (see the
 * `expenses` entry below).
 *
 * AIM's backend is now SINGLE-STACK: Next.js was removed by CUT-01/CUT-02
 * (2026-08-21) and FastAPI serves everything at `/py-api/v1/*`. Every domain
 * below therefore carries `backend: "fastapi"`; the `nextjs` variant is kept
 * only so the indirection (and its test) still describes a backend as data
 * rather than as a hard-coded prefix — a domain moving backend again is a
 * one-word change here, not a client rewrite.
 *
 * This module deliberately does NOT export one global base URL. Every call
 * site names a DOMAIN, and the domain owns which backend and root it is on.
 *
 * `host` is a parameter, not a constant read here: `import.meta.env` is
 * Vite-only and does not exist under Metro or plain Node, so each client
 * supplies its own default (empty string on web, a real origin on mobile) at
 * its own call sites, not in this shared module.
 */

/** Which server a domain is served by. */
export type ApiBackend = "fastapi" | "nextjs";

/** Path prefix each backend mounts its routes under. */
export const BACKEND_PREFIX: Record<ApiBackend, string> = {
  fastapi: "/py-api/v1",
  nextjs: "/api",
};

export interface DomainConfig {
  /** Which backend serves this domain TODAY. The one-line v2.2 flip. */
  backend: ApiBackend;
  /** Domain root, appended to the backend prefix. No trailing slash. */
  root: string;
}

/**
 * The map. Keys are the only names call sites may use.
 *
 * `fastapi` entries are verified against `backend/api/app/routers/*.py` router
 * prefixes; `nextjs` entries against directories in the now-deleted
 * `src/app/api/`.
 */
export const API_DOMAINS = {
  // ── on FastAPI already (new work under D-001, or ported) ──────────────────
  /** Device/browser token auth — `routers/mobile_auth.py`. */
  mobileAuth: { backend: "fastapi", root: "/mobile/auth" },
  /** Session login / whoami — `routers/aim_auth.py`. Also carries the auth
   *  HARDENING endpoints added Wave 3 (agent W12, against W11's in-flight
   *  backend): MFA-login completion `POST /auth/mfa`, `GET/DELETE
   *  /auth/devices` (+ `/auth/devices/revoke-all-others`), and `POST
   *  /auth/change-password`. */
  auth: { backend: "fastapi", root: "/auth" },
  /**
   * Wave 3 (agent W12) — TOTP MFA enrolment (`POST /enrol`, `POST /confirm`,
   * `POST /disable`, `GET /status`), built against W11's documented contract
   * ahead of the backend PR landing (see `rules` below for the established
   * precedent). A top-level root, NOT nested under `auth` — only MFA-login
   * completion lives at `/auth/mfa`; enrolment management lives here.
   * Reconcile if W11 ships a different prefix.
   */
  mfa: { backend: "fastapi", root: "/auth/mfa" },
  /** In-app update check (MOB-17) — `routers/app_version.py`, `GET /mobile/version`. Mobile-only. */
  mobileVersion: { backend: "fastapi", root: "/mobile/version" },
  /** Cross-entity global search — `routers/search.py`. */
  search: { backend: "fastapi", root: "/search" },
  // Rent module — FastAPI-native since v3.0.
  rentProperties: { backend: "fastapi", root: "/properties" },
  rentTenants: { backend: "fastapi", root: "/tenants" },
  rentAgreements: { backend: "fastapi", root: "/agreements" },
  /** Payments mount flat: `/payments`, `/payments/pending`, `/payments/{id}/mark-paid`. */
  rentPayments: { backend: "fastapi", root: "/payments" },
  rentExpenses: { backend: "fastapi", root: "/expenses" },
  rentAnalytics: { backend: "fastapi", root: "/analytics" },
  rentPortfolio: { backend: "fastapi", root: "/portfolio" },
  rentBrokers: { backend: "fastapi", root: "/brokers" },
  rentTds: { backend: "fastapi", root: "/tds" },
  homeLoan: { backend: "fastapi", root: "/homeloan" },
  /**
   * V5-24/V5-25 — general-ledger bank statement import (`routers/
   * statement_import.py`). Distinct from the rent importer at `/statements`,
   * which reconciles against rent Payments; this one creates Income/Expense.
   */
  statementImports: { backend: "fastapi", root: "/statement-imports" },
  /** Phone: Account › Records › Import statement — a bank PDF into one Account
   *  (`routers/account_import.py`): stateless preview, check, commit. */
  accountImport: { backend: "fastapi", root: "/account-import" },
  /** Per-bank PDF column mappings — `routers/bank_profile.py`. Shared by both
   *  importers, which is the point: one mapping per bank, not one per flow. */
  bankProfiles: { backend: "fastapi", root: "/bank-profiles" },

  // ── core AIM ledger ────────────────────────────────────────────────────────
  dashboard: { backend: "fastapi", root: "/dashboard" },
  records: { backend: "fastapi", root: "/records" },
  income: { backend: "fastapi", root: "/income" },
  /**
   * AIM expenses live under `/aim/expenses` (`routers/expense.py`), NOT
   * `/expenses` — that prefix belongs to the RENT PropertyExpense router
   * (`routers/property_expense.py`), which is why `rentExpenses` above points
   * at it. Pointing this entry at `/expenses` silently routed every AIM
   * expense call into the rent module: creates came back 422 demanding
   * `property_id`, export 422'd, and `POST /expenses/recurring` 405'd on every
   * page load. The two roots collide by name and by nothing else.
   */
  expenses: { backend: "fastapi", root: "/aim/expenses" },
  transfers: { backend: "fastapi", root: "/transfers" },
  accounts: { backend: "fastapi", root: "/accounts" },
  /** Phone 1.5.0 — credit cards with billing cycles (`routers/credit_card.py`). "Pay bill" is a plain transfer INTO the card account. */
  creditCards: { backend: "fastapi", root: "/credit-cards" },
  accountsFy: { backend: "fastapi", root: "/accounts-fy" },
  household: { backend: "fastapi", root: "/household" },
  /** 0064 — Operators create families and see their health (`routers/operator.py`). */
  operator: { backend: "fastapi", root: "/operator/households" },
  categories: { backend: "fastapi", root: "/categories" },
  labels: { backend: "fastapi", root: "/labels" },
  budgets: { backend: "fastapi", root: "/budgets" },
  savingsGoals: { backend: "fastapi", root: "/savings-goals" },
  loans: { backend: "fastapi", root: "/loans" },
  itr: { backend: "fastapi", root: "/itr" },
  analytics: { backend: "fastapi", root: "/analytics" },
  yearlySummary: { backend: "fastapi", root: "/yearly-summary" },
  suggestions: { backend: "fastapi", root: "/suggestions" },
  /**
   * V5-35 — named, user-authored entry templates (`routers/template.py`).
   * A SIBLING of `suggestions`, not a replacement: suggestions are DERIVED
   * from 180 days of history, templates are authored up front and
   * archivable. The two compose in the picker.
   */
  templates: { backend: "fastapi", root: "/templates" },
  /**
   * V5-14..V5-21 -- the family document vault (`routers/vault.py`) and the
   * warranty tracker (`routers/warranty.py`). Two roots, ONE feature and one
   * `vault` page permission; they are separate here only because they are
   * separate URL prefixes.
   *
   * There is deliberately no `vaultDocuments` entry: the files hang off the
   * EXISTING polymorphic `rent.document` table, so they are read and
   * downloaded through `documents` below, exactly as rent agreements are.
   */
  vault: { backend: "fastapi", root: "/vault" },
  warranties: { backend: "fastapi", root: "/warranties" },
  /** Polymorphic file attachments -- `routers/document.py`. */
  documents: { backend: "fastapi", root: "/documents" },
  commitments: { backend: "fastapi", root: "/commitments" },
  /**
   * `GET /upcoming?days=` — the forward projection of recurring Income/Expense
   * TEMPLATES plus this month's unpaid commitments, with `totals` and
   * `expectedBalance` computed server-side (`routers/upcoming.py`).
   *
   * Gated on the `dashboard` page permission, not one of its own: it has no
   * page, and its two consumers (the dashboard widget and the planned-payments
   * list) already hold or are denied that grant. So a caller without dashboard
   * view gets 403 here — handle it as such, not as an empty projection.
   *
   * Added 2026-09-04. Both clients previously assembled this URL BY HAND from
   * `API_HOST` + `BACKEND_PREFIX.fastapi` because no entry existed, each
   * leaving a note saying it collapses to `apiUrl("upcoming", …)` the day one
   * lands (`frontend/web/src/components/upcoming/useUpcoming.ts`,
   * `frontend/mobile/src/features/planned/upcoming.ts`). This is that day.
   */
  upcoming: { backend: "fastapi", root: "/upcoming" },
  /**
   * Recurring Rules (2026-10-22, ADR 0002) -- `routers/repeats.py`: list for
   * Records › Expected, skip one repeat, edit / pause / end a rule. Confirming
   * a repeat is an ordinary create on `income` / `expenses` with `repeatOf`.
   */
  repeats: { backend: "fastapi", root: "/repeats" },
  /**
   * V5-14 sub-item 4 — user-defined fields on records (`routers/custom_field.py`).
   * Definitions live in `rent.custom_field`; VALUES reuse the same
   * `rent.entity_option_value` table the option tags do (migration 0024
   * generalised it), which is why there is no separate values domain. Kept
   * distinct from an `/options` root because the option routers already claim a
   * bare `/{option_id}` that anything added there would compete with.
   */
  customFields: { backend: "fastapi", root: "/custom-fields" },
  /**
   * M7 — user-defined option sets (`routers/option.py`), the choice lists a
   * `option_set`-typed custom field points at. Read-only from the custom-fields
   * UI, which offers existing sets rather than creating them.
   */
  options: { backend: "fastapi", root: "/options" },
  admin: { backend: "fastapi", root: "/admin" },
  /** The caller's OWN page grants — `routers/permissions.py` (`GET /me`). The
   *  phone's read-only Sharing rules view for a non-admin reads it. */
  permissions: { backend: "fastapi", root: "/permissions" },
  /**
   * V5-33 — mutual funds / ELSS / equity / gold / silver (`routers/invest.py`).
   * Instruments, holdings, transactions, SIPs, portfolio and the AMFI-backed
   * price refresh all mount flat under this one root.
   */
  invest: { backend: "fastapi", root: "/invest" },
  /**
   * Wave 3 (agent W7/W6) — auto-categorization rules engine
   * (`routers/rules.py`, in flight). CRUD at `/rules`, `/rules/{id}`,
   * `/rules/reorder`; dry-run/apply backfill at `/rules/backfill`. Added by
   * the UI agent (W7) against the documented contract so `apiUrl("rules", …)`
   * type-checks without waiting on the backend PR to land — reconcile the
   * root here if W6 ships a different prefix.
   */
  rules: { backend: "fastapi", root: "/rules" },
  // 1.6.0 — single entry, multiple connections (rent/loan/invest links,
  // deposit ledger, EMI series). FastAPI only.
  links: { backend: "fastapi", root: "/links" },
  // 1.15.0 — Fixed deposits: book / mature / break / portfolio / calculator.
  fd: { backend: "fastapi", root: "/fd" },
  /**
   * 2026-09-30 phone parity — rent managed from the phone: property / tenant /
   * agreement create-edit-archive-delete, tenant documents, payment and
   * schedule corrections, tenant messaging (`routers/rent_manage.py`).
   */
  rentManage: { backend: "fastapi", root: "/rent-manage" },
  /**
   * 2026-10-09 Rent Receipt — one receipt per money received (`routers/
   * receipt.py`): `GET /by-entry/{entryId}`, `GET /by-deposit/{depositEventId}`,
   * `GET /{id}` (+ `/{id}.pdf`), `POST /{id}/send` `{channel, to?}`. Built
   * against the spec's contract while the server lands in parallel; the old
   * per-payment `GET /payments/{id}/receipt` keeps working beside it.
   */
  receipts: { backend: "fastapi", root: "/receipts" },
  /**
   * Wave 4 (agent V12) — Expo push device registration + per-user
   * notification prefs (`routers/push.py`, not yet registered in
   * `app/main.py` — see that router's module docstring). Root is flat:
   * `/register` and `/prefs` (+ `/prefs/reset`), mirroring `mobileAuth`'s
   * shape rather than nesting under a resource id, since there is exactly
   * one device-token row per (user, device) and exactly one prefs envelope
   * per user — nothing here is ever listed or paginated.
   */
  push: { backend: "fastapi", root: "/push" },
  /**
   * Wave 6 (agent B3, against B1's in-flight backend) — Groups: the single
   * concept covering Personal/Family/Rent/Friends/Trip circles and their
   * split-bill expenses (`routers/group.py`, not yet landed). CRUD at
   * `/groups`, `/groups/{id}`; nested `/groups/{id}/members`,
   * `/groups/{id}/expenses`, `/groups/{id}/balances`,
   * `/groups/{id}/settlements`. Reconcile the root here if B1 ships a
   * different prefix.
   */
  groups: { backend: "fastapi", root: "/groups" },
  /**
   * Wave 6 (agent B2) — tokenised, UNAUTHENTICATED group-receipt links
   * (`routers/share_link.py`). Only the public `GET /share/{token}` lives at
   * this root; the authenticated create/list/revoke calls
   * (`POST /groups/{id}/share`, `GET /groups/{id}/shares`,
   * `POST /groups/{id}/share/{shareId}/revoke`) are nested under `groups`
   * above and need no separate domain entry. Callers reaching this domain
   * MUST NOT attach a bearer/session — the whole point is that a login-less
   * friend can open the link with none.
   */
  share: { backend: "fastapi", root: "/share" },
  /**
   * Phone 1.7.0 — per-user preferences (`routers/preference.py`): theme,
   * dashboard, privacy and the `/alerts` section (reminders & alerts).
   */
  preferences: { backend: "fastapi", root: "/preferences" },
  /**
   * Per-user privacy settings and the reveal gate (`routers/privacy.py`):
   * `GET/PUT ""`, `POST /reveal` (password re-auth -> short-lived grant). Own
   * data only, no `?all=true`. The web's `lib/privacy/api.ts` and the phone's
   * `features/privacy/reveal.ts` both post here.
   */
  privacy: { backend: "fastapi", root: "/privacy" },
  /**
   * Self-service Telegram link (`routers/telegram_self.py`): `GET/DELETE
   * /link`, `POST /link/code`, `POST /test`. Own data only. The admin half
   * (bot token, linked users) hangs off `admin` at `/settings/telegram`.
   */
  telegram: { backend: "fastapi", root: "/telegram" },
  /**
   * Phone 1.7.0 — the 30-day Recycle Bin (`routers/recycle_bin.py`). The audit
   * trail's Restore goes through `POST /recycle-bin/{entity}/{id}/restore`.
   */
  recycleBin: { backend: "fastapi", root: "/recycle-bin" },
  /**
   * The in-app feed behind the header bell (`routers/notification.py`):
   * `GET ""` (the feed), `GET /unread-count`, `POST /read`, `POST /read-all`.
   * The phone's "Family activity" panel reads it (2026-09-30).
   */
  notifications: { backend: "fastapi", root: "/notifications" },
} as const satisfies Record<string, DomainConfig>;

export type ApiDomain = keyof typeof API_DOMAINS;

/** Base URL for a domain, e.g. `/py-api/v1/mobile/auth`. */
export function domainBaseUrl(domain: ApiDomain, host = ""): string {
  const cfg: DomainConfig = API_DOMAINS[domain];
  return `${host.replace(/\/+$/, "")}${BACKEND_PREFIX[cfg.backend]}${cfg.root}`;
}

export type QueryValue = string | number | boolean | null | undefined;

/** Absolute-or-relative URL for a path inside a domain, with query params. */
export function apiUrl(
  domain: ApiDomain,
  path = "",
  query?: Record<string, QueryValue>,
  host = "",
): string {
  const suffix = path && !path.startsWith("/") ? `/${path}` : path;
  const base = `${domainBaseUrl(domain, host)}${suffix}`;
  if (!query) return base;

  const parts: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    // Empty string is dropped as well as null/undefined: an empty `?q=` filters
    // on the empty string server-side rather than meaning "no filter".
    if (value === null || value === undefined || value === "") continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `${base}?${parts.join("&")}` : base;
}

/** Which backend a domain is on — useful for diagnostics screens. */
export function backendOf(domain: ApiDomain): ApiBackend {
  return API_DOMAINS[domain].backend;
}
